import React from "react";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#7c3aed";
const RECRUIT_ROLES = ["admin", "admin_assistant", "hr", "recruiter"] as const;

export default function Recruitment() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>🎯</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>Recruitment</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Recruitment</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 640, lineHeight: 1.6 }}>
          The full hiring pipeline — AI-drafted job postings, AI-scored candidates, interview scheduling,
          async video screening, and offer letters.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "jobs", label: "Jobs & AI JD Generation" },
        { id: "candidates", label: "Candidates & AI Scoring" },
        { id: "pipeline", label: "Job Pipeline (Kanban)" },
        { id: "talent-pool", label: "Talent Pool" },
        { id: "interview-hub", label: "Interview Hub" },
        { id: "screening", label: "AI Video Screening" },
        { id: "offers", label: "Offer Letters" },
        { id: "careers", label: "Public Careers Page" },
      ]} />

      <Section id="jobs" title="Jobs & AI JD Generation">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Create and manage job postings. Use AI to generate a full job description from a prompt or an
          uploaded PDF, and post directly to LinkedIn.
        </p>
        <Img src="/docs/screenshots/hr-jobs.png" alt="Jobs list" />
        <Steps items={[
          "Click Jobs in the sidebar, then New Job — AI Generate is selected by default.",
          "Type a short prompt (e.g. \"Senior React Developer, 5+ years experience, Bangkok, hybrid work\") or paste full client requirements, or upload a PDF/Word doc instead.",
          "Expand Optional Details to give the AI hints — experience level, location, salary range, language requirements.",
          "Click Generate Job Description — the AI writes the full JD and a LinkedIn post from what you entered.",
        ]} />
        <Img src="/docs/screenshots/hr-ai-jd-generation.png" alt="AI Generate job description form" />
        <Steps items={[
          "Review and edit the generated description as needed, then save.",
          "To post to LinkedIn: connect your LinkedIn account in Settings → Integrations first, then click Post to LinkedIn on the job.",
          "Click Career Page on a job to copy its public application link — share it anywhere to collect candidates.",
        ]} />
        <Callout
          icon="🤖"
          title="AI JD generation"
          body="The AI uses your prompt or uploaded PDF to generate a structured job description with responsibilities, requirements, and benefits. You can regenerate as many times as needed before saving."
        />
      </Section>

      <Section id="candidates" title="Candidates & AI Scoring">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Candidates in the sidebar shows every applicant across all jobs in one searchable, filterable list —
          this is your main working view for reviewing and processing applicants.
        </p>
        <Img src="/docs/screenshots/hr-candidates.png" alt="Candidates list" />
        <Steps items={[
          "Click Candidates in the sidebar.",
          "Filter by stage using the tabs (All, New, Reviewed, Shortlisted, Hired, Rejected), or search by name/email/job.",
          "The AI automatically scores each CV (0–100) based on relevance to its job.",
          "Click a candidate's row to open their detail panel.",
        ]} />
        <Img src="/docs/screenshots/hr-candidate-detail.png" alt="Candidate detail panel with AI score breakdown" />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          The <strong>Overview</strong> tab shows the AI's written assessment and a full score breakdown —
          Skills Match, Experience, Education, and Overall Profile — each with the reasoning behind it, plus
          quick tags like education and career progression.
        </p>
        <Img src="/docs/screenshots/hr-candidate-actions.png" alt="Candidate detail panel Actions tab" />
        <Steps items={[
          "The Actions tab is where you work the candidate: Move To buttons change their stage.",
          "Files: download the AI-formatted CV as PDF or Word, the original upload, a shareable Profile PDF, or Regenerate the formatted version.",
          "Workflow: Schedule Interview, Propose Slots, draft an Offer Letter, or Send Screening for an AI video interview.",
          "AI Tools: generate Interview Questions, or draft an Outreach Email (pick a tone — Warm, Formal, etc.) and view past outreach history.",
          "Manage: Edit Details or Remove CV.",
          "Select multiple candidates from the list with the checkboxes to bulk-update their status at once, or export the list to CSV.",
        ]} />
        <Callout
          icon="🤖"
          title="AI CV scoring"
          body="Scoring happens automatically when a CV is uploaded. The AI analyses skills, experience, education, and overall profile against the job's requirements, with a written explanation for each score component. Scores above 70 are strong matches."
        />
      </Section>

      <Section id="pipeline" title="Job Pipeline (Kanban)">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          For a visual, per-job view, open any job from the Jobs page — each job has its own drag-and-drop
          pipeline board with the same stage columns (New, Reviewed, Shortlisted, Hired, Rejected).
        </p>
        <Img src="/docs/screenshots/hr-job-pipeline.png" alt="Job pipeline Kanban board" />
        <Steps items={[
          "Click Jobs in the sidebar, then click into a specific job.",
          "Scroll to the pipeline board — candidates for that job appear as cards in their stage column.",
          "Drag a card into a different column to move that candidate to that stage — saves immediately, no confirmation needed.",
          "Click a card to open the same detail panel and actions available from the Candidates list.",
        ]} />
      </Section>

      <Section id="talent-pool" title="Talent Pool">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Every candidate you've ever added stays searchable in the Global Talent Pool — across all jobs,
          past and present.
        </p>
        <Img src="/docs/screenshots/rec-talent-pool.png" alt="Global Talent Pool" />
        <Steps items={[
          "Click Talent Pool in the sidebar.",
          "Search by name, email, role, or location, and filter by minimum/maximum years of experience.",
          "Existing AI scores are shown instantly — re-searching the pool doesn't count against your CVs processed quota.",
          "From an open job's pipeline, click Find Matches to run an AI compatibility search against the whole pool for that specific role — each match shows a compatibility %, match reason, and any potential concerns.",
          "On a match, click Draft Email for an AI-written outreach email, or Add to Pipeline to bring them straight into that job's Kanban board.",
        ]} />
        <Callout
          icon="💡"
          title="Reuse before you re-source"
          body="Before posting a new job, check the Talent Pool first — a past applicant to a different role might already be a strong match, without needing a fresh CV upload."
        />
      </Section>

      <Section id="interview-hub" title="Interview Hub">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          A single view of every interview across all jobs and candidates — no need to open each job separately.
        </p>
        <Img src="/docs/screenshots/rec-interview-hub.png" alt="Interview Hub" />
        <Steps items={[
          "Click Interview Hub in the sidebar.",
          "Filter by status (Scheduled, Confirmed, Expired, etc.) or search by candidate or job title.",
          "Click + New Interview, then choose a candidate and job.",
          "Schedule Now: pick a specific date and time directly.",
          "Automatic Scheduling: propose one or more time slots and let the candidate confirm which works for them — the status updates to Confirmed once they respond.",
        ]} />
      </Section>

      <Section id="screening" title="AI Video Screening">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Send candidates a self-serve video interview before you ever speak to them — they record answers to
          AI-generated questions on their own time, and the AI scores their responses for you to review.
        </p>
        <Img src="/docs/screenshots/rec-screening.png" alt="AI Screening" />
        <Steps items={[
          "Click AI Screening in the sidebar, then select a job.",
          "Click Generate Questions — the AI writes a set of interview questions tailored to the job description. Edit or add your own, then save the template.",
          "Invite a candidate: this creates a screening session and sends them a one-time link — no account or login required on their end.",
          "The candidate opens the link, runs a quick connection check, and records a video answer to each question in turn.",
          "Once they submit, review the session: watch each answer's video, see the AI's score, and read the summary.",
          "If needed, click Rescore to re-run the AI evaluation, Resend Invite if the candidate hasn't completed it yet, or Export to download session data.",
        ]} />
        <Callout
          icon="🎥"
          title="Candidate requirements"
          body="Candidates need a device with a camera and microphone and a modern browser. The link runs a short speed/connection check before recording starts, so a poor connection is caught early instead of failing mid-answer."
        />
      </Section>

      <Section id="offers" title="Offer Letters">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Generate professional offer letters for candidates. The AI drafts the full letter from candidate and
          job data, which you can edit in-browser before sending.
        </p>
        <Img src="/docs/screenshots/hr-offers.png" alt="Offers list" />
        <Steps items={[
          "Click Offers in the sidebar, then New Offer or click Create Offer from a candidate's profile.",
          "Select the candidate and job. Fill in the offered salary, start date, and any special terms.",
          "Click AI Draft — the system generates a full offer letter using the candidate and job details.",
          "Edit the letter directly in the browser.",
          "Click Send to Candidate — the letter is emailed as a PDF.",
          "Track status: Draft → Sent → Accepted / Declined.",
        ]} />
      </Section>

      <Section id="careers" title="Public Careers Page">
        <AvailableTo roles={[...RECRUIT_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Every open job automatically appears on your company's public careers page — a no-login page
          candidates can browse and apply from directly.
        </p>
        <Steps items={[
          "Open a job and click Career Page to copy its public application link.",
          "Share the link anywhere — job boards, social media, your own website.",
          "A candidate opens the link, fills in their details, and uploads a CV — this creates a new Candidates entry in that job's pipeline automatically, already AI-scored.",
        ]} />
      </Section>
    </DocsLayout>
  );
}
